import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/adminService';
import LogoLoader from "../Common/LogoLoader";
import AdminReassignModal from "./AdminReassignModal";

function sellerName(validation) {
  const user = validation.user || {};
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || "Seller";
}

function sellerPlace(validation) {
  const user = validation.user || {};
  return [user.homeAddress, user.state].filter(Boolean).join(", ") || "Address not set";
}

function agentLabel(agent) {
  if (!agent) return "Unassigned";
  return agent.agentDetails?.agent_name || [agent.firstName, agent.lastName].filter(Boolean).join(" ") || "Agent";
}

const PendingValidations = () => {
  const [validations, setValidations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState({
    totalQuantity: 0,
    totalAgents: 0,
    totalStates: 0,
    stateBreakdown: [],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 450);
  const [stateFilter, setStateFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
  });
  const [agents, setAgents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null);
  const [ready, setReady] = useState(false);

  const NIGERIAN_STATES = [
    "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno",
    "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "Gombe", "Imo",
    "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos",
    "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers",
    "Sokoto", "Taraba", "Yobe", "Zamfara", "Abuja"
  ];

  useEffect(() => {
    fetchValidations();
  }, [currentPage, debouncedSearch, stateFilter, agentFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [debouncedSearch, stateFilter, agentFilter]);

  useEffect(() => {
    adminService
      .getAllAgents()
      .then((list) => setAgents((list || []).filter((agent) => agent.isAgent && agent.role === "agent")))
      .catch(() => setAgents([]));
  }, []);

  const fetchValidations = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        state: stateFilter,
        agent: agentFilter
      };
      
      const data = await adminService.getPendingValidations(params);
      setValidations(data.validations || []);
      setStats(data.stats || {});
      setPagination(
        data.pagination || {
          totalPages: 1,
          totalItems: (data.validations || []).length,
        }
      );
    } catch (error) {
      console.error('Error fetching validations:', error);
      setMessage('Failed to fetch validations');
    } finally {
      setLoading(false);
      setReady(true);
    }
  };

  const openAssign = (rows) => {
    const list = Array.isArray(rows) ? rows : [rows];
    const ids = list.map((row) => row._id || row.id).filter(Boolean);
    if (!ids.length) return;
    setAssignTarget({
      binIds: ids,
      currentAgentId: ids.length === 1 ? list[0].selectedAgent?._id : null,
      sellerLabel: ids.length === 1 ? sellerName(list[0]) : `${ids.length} selected orders`,
      sellerPlace: ids.length === 1 ? sellerPlace(list[0]) : "",
    });
  };

  const fetchStats = async () => {
    try {
      // This would be replaced with actual API call
      // const response = await validationService.getValidationStats();
      // setStats(response.data);
      
      // Mock stats for now
      setStats({
        totalQuantity: 350,
        totalAgents: 2,
        totalStates: 2,
        stateBreakdown: [
          { state: "Lagos", quantity: 150, percentage: 42.9 },
          { state: "Abuja", quantity: 200, percentage: 57.1 }
        ]
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const totalPages = pagination.totalPages || 1;
  const totalItems = pagination.totalItems ?? validations.length;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex =
    totalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalItems);
  const currentValidations = validations;
  const pageIds = currentValidations.map((validation) => validation._id || validation.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleAllOnPage = () => {
    setSelectedIds((current) => {
      if (allOnPageSelected) return current.filter((id) => !pageIds.includes(id));
      return [...new Set([...current, ...pageIds])];
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (!ready && loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Pending Validations</h1>
        <div className="text-sm text-gray-500">
          {totalItems} validations
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CubeIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Quantity</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalQuantity} kg</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Agents</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalAgents}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapPinIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total States</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalStates}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CubeIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Bins</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalBins || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State Breakdown */}
      {(stats.quantityByState || []).length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quantity by State</h3>
            <div className="space-y-3">
              {(stats.quantityByState || []).map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{item._id}</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${((item.quantity / stats.totalQuantity) * 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500 w-16 text-right">{item.quantity} kg</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`rounded-lg px-4 py-3 text-sm ${message.startsWith("Failed") || message.startsWith("Could") ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"}`}>{message}</div>
      )}

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search seller, address, agent, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
            />
          </div>

          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">All seller states</option>
            {NIGERIAN_STATES.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">All assigned agents</option>
            {agents.map((agent) => (
              <option key={agent._id} value={agent._id}>{agentLabel(agent)}</option>
            ))}
          </select>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <p className="text-sm font-medium text-green-900">{selectedIds.length} selected</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() =>
                setAssignTarget({
                  binIds: selectedIds,
                  currentAgentId: null,
                  sellerLabel: `${selectedIds.length} selected orders`,
                  sellerPlace: "",
                })
              }
              className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              Assign agent
            </button>
          </div>
        </div>
      )}

      {/* Validations Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Pending Validations</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            List of all pending validations awaiting agent approval
          </p>
        </div>
        
        {(currentValidations || []).length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No validations found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || stateFilter || agentFilter 
                ? 'Try adjusting your search criteria.' 
                : 'No pending validations at the moment.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      aria-label="Select all orders on this page"
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seller
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seller address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Kg
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Materials
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentValidations.map((validation) => {
                  const rowId = validation._id || validation.id;
                  const agent = validation.selectedAgent;
                  const agentArea = [
                    agent?.agentDetails?.streetAddress,
                    agent?.agentDetails?.localGovernmentAddress,
                    agent?.state,
                  ].filter(Boolean).join(", ");
                  return (
                  <tr key={rowId} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(rowId)}
                        onChange={() => toggleOne(rowId)}
                        aria-label={`Select ${sellerName(validation)}`}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{sellerName(validation)}</div>
                      <div className="text-sm text-gray-500">{validation.user?.phoneNumber || "No phone"}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{validation.user?.homeAddress || "Address not set"}</div>
                      <span className="mt-1 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {validation.user?.state || "State not set"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{agentLabel(agent)}</div>
                      <div className="text-sm text-gray-500">{agent?.phoneNumber || "No phone"}</div>
                      <div className="max-w-xs whitespace-normal text-sm text-gray-500">{agentArea || "Address not set"}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {validation.totalQuantity || validation.totalKg || 0} kg
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {validation.materials?.map((material, index) => (
                          <div key={index} className="text-sm text-gray-900">
                            {material.material?.name || material.name}: {material.quantity}kg (₦{material.pricePerKg || material.price || 0}/kg)
                          </div>
                        )) || 'No materials'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(validation.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={() => openAssign(validation)}
                        className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                      >
                        Change agent
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && totalItems > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex}</span> to{' '}
                  <span className="font-medium">{endIndex}</span> of{' '}
                  <span className="font-medium">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-green-50 border-green-500 text-green-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <AdminReassignModal
        isOpen={Boolean(assignTarget)}
        onClose={() => setAssignTarget(null)}
        binIds={assignTarget?.binIds || []}
        currentAgentId={assignTarget?.currentAgentId}
        sellerLabel={assignTarget?.sellerLabel || ""}
        sellerPlace={assignTarget?.sellerPlace || ""}
        onSuccess={() => {
          setSelectedIds([]);
          setMessage("Agent assignment updated. The seller and both agents have been notified.");
          fetchValidations();
        }}
      />
    </div>
  );
};

export default PendingValidations;
